const ALLOWED_DOMAINS = {
  TESTING: [
    "http://localhost:3006",
    "http://localhost:3000",
    "http://localhost:3007",
  ],
  DEVELOPMENT: [
    "https://pallas-dev.com",
    "https://www.pallas-dev.com",
    "https://cashflowportal.dev",
    "https://www.cashflowportal.dev",
  ],
  STAGING: [
    "https://pallas-staging.com",
    "https://www.pallas-staging.com",
    "https://cashflowportal-staging.net",
    "https://www.cashflowportal-staging.net",
  ],
  PRODUCTION: [
    "https://investbase.com",
    "https://www.investbase.com",
    "https://cashflowportal.com",
    "https://www.cashflowportal.com",
  ],
};

const MARKETPLACE_DOMAINS = {
  TESTING: ["http://localhost:3006"],
  DEVELOPMENT: ["https://pallas-dev.com", "https://www.pallas-dev.com"],
  STAGING: ["https://pallas-staging.com", "https://www.pallas-staging.com"],
  PRODUCTION: ["https://investbase.com", "https://www.investbase.com"],
};

const PORTAL_DOMAINS = {
  TESTING: ["http://localhost:3000"],
  DEVELOPMENT: ["https://cashflowportal.dev", "https://www.cashflowportal.dev"],
  STAGING: [
    "https://cashflowportal-staging.net",
    "https://www.cashflowportal-staging.net",
  ],
  PRODUCTION: ["https://cashflowportal.com", "https://www.cashflowportal.com"],
};

const PORTAL_HOSTS = {
  TESTING: ["localhost"],
  DEVELOPMENT: ["cashflowportal.dev"],
  STAGING: ["cashflowportal-staging.net"],
  PRODUCTION: ["cashflowportal.com"],
};

const normalizeOrigin = (origin) => {
  if (!origin) return "";
  try {
    return new URL(origin).origin;
  } catch {
    return origin.replace(/\/+$/, "");
  }
};

const normalizeOrigins = (origins) => [
  ...new Set(origins.map(normalizeOrigin).filter(Boolean)),
];

export const getWriteOnlyDomains = () => {
  if (!process.env.REACT_APP_WRITE_ONLY_SSO_DOMAINS) {
    return [];
  }
  return normalizeOrigins(
    process.env.REACT_APP_WRITE_ONLY_SSO_DOMAINS.split(","),
  );
};

export const getAllowedDomains = () => {
  if (process.env.REACT_APP_ENV === "production") {
    return normalizeOrigins(ALLOWED_DOMAINS.PRODUCTION);
  } else if (process.env.REACT_APP_ENV === "staging") {
    return normalizeOrigins(ALLOWED_DOMAINS.STAGING);
  } else if (process.env.REACT_APP_ENV === "development") {
    return normalizeOrigins(ALLOWED_DOMAINS.DEVELOPMENT);
  }
  return normalizeOrigins(ALLOWED_DOMAINS.TESTING);
};

const getPortalHostsForEnv = () => {
  if (process.env.REACT_APP_ENV === "production") {
    return PORTAL_HOSTS.PRODUCTION;
  } else if (process.env.REACT_APP_ENV === "staging") {
    return PORTAL_HOSTS.STAGING;
  } else if (process.env.REACT_APP_ENV === "development") {
    return PORTAL_HOSTS.DEVELOPMENT;
  }
  return PORTAL_HOSTS.TESTING;
};

const originMatchesPortalHost = (origin) => {
  const normalizedOrigin = normalizeOrigin(origin);
  if (!normalizedOrigin) return false;

  let parsedOrigin;
  try {
    parsedOrigin = new URL(normalizedOrigin);
  } catch {
    return false;
  }

  if (
    process.env.REACT_APP_ENV === "production" ||
    process.env.REACT_APP_ENV === "staging" ||
    process.env.REACT_APP_ENV === "development"
  ) {
    if (parsedOrigin.protocol !== "https:") return false;
  } else if (
    parsedOrigin.protocol !== "http:" ||
    parsedOrigin.port !== "3000"
  ) {
    return false;
  }

  return getPortalHostsForEnv().some(
    (host) =>
      parsedOrigin.hostname === host ||
      parsedOrigin.hostname.endsWith(`.${host}`),
  );
};

export const isAllowedDomain = (origin) => {
  const normalizedOrigin = normalizeOrigin(origin);
  return (
    getAllowedDomains().includes(normalizedOrigin) ||
    originMatchesPortalHost(normalizedOrigin)
  );
};

const getDomainsForEnv = (domains) => {
  if (process.env.REACT_APP_ENV === "production") {
    return normalizeOrigins(domains.PRODUCTION);
  } else if (process.env.REACT_APP_ENV === "staging") {
    return normalizeOrigins(domains.STAGING);
  } else if (process.env.REACT_APP_ENV === "development") {
    return normalizeOrigins(domains.DEVELOPMENT);
  }
  return normalizeOrigins(domains.TESTING);
};

export const getCashflowMarketplaceUrls = () => {
  return getDomainsForEnv(MARKETPLACE_DOMAINS);
};

export const getCashflowPortalUrls = () => {
  return getDomainsForEnv(PORTAL_DOMAINS);
};

export const getCashflowMarketplaceUrl = () => {
  return getCashflowMarketplaceUrls()[0];
};

export const getCashflowPortalUrl = () => {
  return getCashflowPortalUrls()[0];
};

export const isCashflowMarketplaceOrigin = (origin) => {
  return getCashflowMarketplaceUrls().includes(normalizeOrigin(origin));
};

export const getSafeRedirectUrl = (redirectUrl, fallback = "/app") => {
  if (
    typeof redirectUrl === "string" &&
    redirectUrl.startsWith("/") &&
    !redirectUrl.startsWith("//")
  ) {
    return redirectUrl;
  }

  return fallback;
};

export const parentDomain = {
  parentOrigin: normalizeOrigin(document.referrer),
};
export const encodeOrigin = () => {
  return encodeURIComponent(parentDomain.parentOrigin);
};

export const sendPayload = (
  payload,
  targetOrigin = parentDomain.parentOrigin,
) => {
  const normalizedTargetOrigin = normalizeOrigin(targetOrigin);
  if (isAllowedDomain(normalizedTargetOrigin)) {
    window.top?.postMessage(payload, normalizedTargetOrigin);
  }
};
